import {css, CSSProperties, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

interface ComponentButtonProps {
  'aria-label'?: string;
  onClick: () => void;
  className?: string;
  disabled?: boolean;
  label?: string;
  dataTestAttrs?: DataTestAttrs;
}

interface IconButtonProps extends ComponentButtonProps {
  icon: BenchlingIconName;
  buttonAphroditeStyle?: object;
  buttonStyle?: ButtonStyle;
}

const wrapButton = (button: JSX.Element, label?: string): JSX.Element =>
  label == null ? button : <Tooltip title={label}>{button}</Tooltip>;

export const IconButton = (props: IconButtonProps): JSX.Element => {
  const button = (
    <Button
      aria-label={props['aria-label']}
      className={classNames(css(props.buttonAphroditeStyle), props.className)}
      buttonStyle={props.buttonStyle || ButtonStyle.TERTIARY_GRAY}
      onClick={props.onClick}
      size={ButtonSize.SMALL}
      disabled={props.disabled}
      icon={props.icon}
      dataTestAttrs={props.dataTestAttrs}
    />
  );
  return wrapButton(button, props.label);
};

export function AddComponentButton(props: ComponentButtonProps): JSX.Element {
  return (
    <IconButton
      {...props}
      aria-label="Add"
      icon="compose"
      buttonStyle={ButtonStyle.TERTIARY_WHITE}
      buttonAphroditeStyle={styles.addButton}
    />
  );
}

export function EditComponentButton(props: ComponentButtonProps): JSX.Element {
  return <IconButton {...props} aria-label="Edit" icon="edit" buttonAphroditeStyle={styles.grayButton} />;
}

export function RemoveComponentButton(props: ComponentButtonProps): JSX.Element {
  return (
    <IconButton {...props} aria-label="Remove" icon="delete" buttonAphroditeStyle={styles.removeButton} />
  );
}

const basePassiveButtonStyle = (color: string): CSSProperties => ({
  color,
  background: 'transparent',
  border: 'none',
  boxShadow: 'none',
});

const passiveButtonStyle = (baseColor: string, activeColor?: string): CSSProperties => ({
  ...basePassiveButtonStyle(baseColor),
  ':hover': {
    ...basePassiveButtonStyle(activeColor || baseColor),
  },
  ':active': {
    ...basePassiveButtonStyle(activeColor || baseColor),
  },
  ':focus': {
    ...basePassiveButtonStyle(activeColor || baseColor),
  },
});

const styles = StyleSheet.create({
  addButton: passiveButtonStyle(Colors.BLUE_LIGHT),
  grayButton: passiveButtonStyle(Colors.GRAY_DARK),
  removeButton: passiveButtonStyle(Colors.GRAY_DARK, Colors.RED_LIGHT),
});
